import { useApiMutationAdminSearchOrdersByCampaignWithOrderId } from '@hooks-api/fortune-fastpass/use-api-admin-search-orders-by-campaign'
import { type CampaignAdminErrorResponse } from '@hooks-api/types/apiErrorResponse'
import { FastpassOrderStatus } from '@hooks-api/types/dataModel/fpOrder'
import { PaymentMethodMap, PaymentMethodType } from '@hooks-api/types/dataModel/payment'
import { Button } from '@pets-ui/Button/Button'
import { Dialog } from '@pets-ui/Dialog/Dialog'
import { DialogActions } from '@pets-ui/DialogActions/DialogActions'
import { DialogContent } from '@pets-ui/DialogContent/DialogContent'
import { DialogHeader } from '@pets-ui/DialogHeader/DialogHeader'
import { HStack } from '@pets-ui/HStack/HStack'
import { ErrorFilled } from '@pets-ui/icons'
import { useSnackbar } from '@pets-ui/Snackbar/useSnackbar'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { AxiosError } from 'axios'
import { useCallback, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom-v5'
import { getClient } from '../clients'
import { useCampaign } from '../hooks/use-campaign'
import { setOrder } from '../store/reducer/admin-reducer'
import style from '../style/components/manual-get-order.module.scss'

export const ManualGetOrder = () => {
  const { t } = useTranslation()
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [isButtonEnable, setIsButtonEnable] = useState(false)
  const { campaignCodeFromUrl } = useCampaign()
  const history = useHistory()
  const dispatch = useDispatch()
  const { openSnackbar } = useSnackbar()
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const handleDialogClose = () => setIsDialogOpen(false)
  const handleDialogOpen = () => setIsDialogOpen(true)
  const [paymentMethodType, setPaymentMethodType] = useState<string>()

  const adminToken = JSON.parse(sessionStorage.getItem('admin') || '{}')

  const handleInputChange = () => {
    const inputValue = inputRef.current?.value
    setIsButtonEnable(inputValue !== '')
  }

  const { trigger: triggerGetOrders, isMutating } = useApiMutationAdminSearchOrdersByCampaignWithOrderId()

  const getOrder = useCallback(async () => {
    if (!adminToken?.token) {
      return
    }

    try {
      const { data } = await triggerGetOrders({ id: inputRef.current?.value, code: campaignCodeFromUrl })

      dispatch(setOrder(data.data))
      const orderStatus = data.data.status
      const paymentMethodType = data.data.payment_method.type
      if (paymentMethodType !== PaymentMethodType.OFFLINE_PAYMENT && orderStatus !== FastpassOrderStatus.COMPLETED) {
        setPaymentMethodType(PaymentMethodMap[paymentMethodType])
        handleDialogOpen()
        return
      }

      const isOrderCompleted = data.data.status === FastpassOrderStatus.COMPLETED
      const isConfirmedGiveaway = data.data.item_confirmed

      if (isOrderCompleted) {
        if (!isConfirmedGiveaway) {
          history.push(`/admin/${campaignCodeFromUrl}/admin-order-check/${inputRef.current?.value}`)
          return
        }
        else {
          history.push(`/admin/${campaignCodeFromUrl}/admin-checked`)
          return
        }
      }

      history.push(`/admin/${campaignCodeFromUrl}/admin-review/${inputRef.current?.value}`)
    }
    catch (error) {
      if (error instanceof AxiosError) {
        const errorData = error.response?.data as CampaignAdminErrorResponse
        if (errorData.error.code === 5600) {
          openSnackbar({
            message: '憑證過期，請重新登入！',
            anchorOrigin: { horizontal: 'center', vertical: 'top' },
            icon: <ErrorFilled color={colors.red600} />,
          })
          sessionStorage.removeItem('admin')
          getClient('fastpassAdmin').defaults.headers['admin-access-token'] = ''
          history.push(`/admin/${campaignCodeFromUrl}/admin-login`)
          return
        }
        if (errorData.error.code === 5300 || errorData.error.code === 5500) {
          openSnackbar({
            message: '訂單不存在',
            anchorOrigin: { horizontal: 'center', vertical: 'top' },
            icon: <ErrorFilled color={colors.red600} />,
          })
        }
      }
    }
  }, [adminToken?.token, campaignCodeFromUrl, dispatch, history, openSnackbar, triggerGetOrders])

  return (
    <>
      <div className={style.wrapper}>
        <p className={style.title}>{t('order.tips')}</p>
        <HStack width="100%" gap={2} flexWrap="nowrap">
          <div className={style['input-wrapper']}>
            <input
              className={style.input}
              ref={inputRef}
              type="text"
              name="order-id"
              placeholder={t('order.inputPlaceholder') || ''}
              data-testid="input-admin-checkout-order-id"
              onChange={handleInputChange}
            />
          </div>
          <Button
            borderRadius="100px"
            disabled={!isButtonEnable || isMutating}
            onClick={getOrder}
            loading={isMutating}
            data-testid="button-admin-checkout-submit-order-id"
          >
            {t('order.submit')}
          </Button>
        </HStack>
      </div>
      <Dialog open={isDialogOpen} onClose={handleDialogClose} maxWidth={320}>
        <DialogHeader>線上付款未完成</DialogHeader>
        <DialogContent>
          訂單付款方式為
          {paymentMethodType}
          ，未完成付款，請顧客透過簡訊連結重新選擇付款方式！
        </DialogContent>
        <DialogActions>
          <Button fullWidth onClick={handleDialogClose}>我知道了</Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
